import React, { useState } from 'react';
import type { PersonalityState } from '../../receptionist-settings/types';
import { RCPT_SETTINGS_VOICE_PRESETS } from '../../receptionist-settings/mockData';
import type { VoicePreset } from '../../receptionist-settings/types';
import { Icon } from '../Icon/Icon';
import {
  SettingsDirtyBlock,
  SettingsFieldRow,
  SettingsPageHeader,
  SettingsSectionHeader,
  SettingsSlider,
  SettingsToggle,
} from './SettingsShared';

export interface PersonalitySettingsPageProps {
  state: PersonalityState;
  setState: React.Dispatch<React.SetStateAction<PersonalityState>>;
  initial: PersonalityState;
}

function VoiceMiniWave({ playing }: { playing: boolean }) {
  const heights = [8, 14, 18, 14, 22, 12, 18, 10, 16, 8];
  return (
    <div className="flex h-[22px] items-center gap-0.5">
      {heights.map((h, i) => (
        <span
          key={i}
          className="w-0.5 rounded-[999px] transition-opacity duration-200"
          style={{
            height: h,
            background: playing ? 'var(--set-green)' : 'var(--ink-3)',
            opacity: playing ? 1 : 0.35,
          }}
        />
      ))}
    </div>
  );
}

function VoiceRow({
  voice,
  selected,
  playing,
  onSelect,
  onPlay,
  isLast,
}: {
  voice: VoicePreset;
  selected: boolean;
  playing: boolean;
  onSelect: (id: string) => void;
  onPlay: (id: string) => void;
  isLast: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(voice.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(voice.id);
        }
      }}
      className={[
        'grid cursor-pointer grid-cols-[auto_1fr_auto_auto] items-center gap-3.5 py-3.5 pr-6 transition-colors duration-[140ms]',
        selected ? 'bg-[var(--set-green-wash)] pl-[21px]' : 'bg-transparent pl-6',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
      style={{ borderLeft: selected ? '3px solid var(--set-green)' : '3px solid transparent' }}
    >
      <div
        className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[999px] border-[1.5px]"
        style={{
          borderColor: selected ? 'var(--set-green)' : 'var(--cream-2)',
          background: selected ? 'var(--set-green)' : 'var(--cream-1)',
        }}
      >
        {selected ? <span className="h-1.5 w-1.5 rounded-[999px] bg-[var(--cream-1)]" /> : null}
      </div>

      <div className="min-w-0">
        <div className="mb-0.5 flex flex-wrap items-center gap-2">
          <span className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
            {voice.name}
          </span>
          <span className="set-pill-neutral text-[10px]">{voice.type}</span>
          <span className="text-[11.5px] font-medium text-[var(--ink-2)]">· {voice.tone}</span>
        </div>
        <div className="text-[12.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">{voice.blurb}</div>
      </div>

      <VoiceMiniWave playing={playing} />

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPlay(voice.id);
        }}
        className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[999px] border-0 transition-colors duration-[160ms]"
        style={{
          background: playing ? 'var(--set-green)' : 'var(--surface-mute)',
          color: playing ? 'var(--cream-1)' : 'var(--ink-1)',
          boxShadow: playing ? '0 2px 8px var(--set-green-soft)' : 'none',
        }}
      >
        {playing ? (
          <Icon kind="pause" size={13} stroke={2} />
        ) : (
          <Icon kind="play" size={11} stroke={2} className="ml-px" />
        )}
      </button>
    </div>
  );
}

export function PersonalitySettingsPage({ state, setState, initial }: PersonalitySettingsPageProps) {
  const upd = (key: keyof PersonalityState, value: PersonalityState[keyof PersonalityState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof PersonalityState) => state[key] !== initial[key];
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);

  const handlePlay = (id: string) => {
    setPlayingVoice(id);
    setTimeout(() => setPlayingVoice(null), 2500);
  };

  return (
    <>
      <SettingsPageHeader
        eyebrow="Setup · personality"
        title="Personality &"
        italic="character"
        description="Who patients meet when they reach your practice through Elva. These settings drive the voice and instructions sent to her voice engine on every session."
      />

      <section className="set-fade-up set-d-1 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="01"
            title="What should we call her?"
            sub="Used in greetings, voicemails, and when Elva introduces herself."
          />
          <SettingsFieldRow
            title="Name"
            description="Keep it short — 1–2 syllables work best for voice. Most practices stick with Elva."
            isDirty={dirty('name')}
            last
          >
            <input
              className="set-input w-[280px] shadow-[var(--sh-1)]"
              value={state.name}
              onChange={(e) => upd('name', e.target.value)}
            />
          </SettingsFieldRow>
        </div>
      </section>

      <section className="set-fade-up set-d-2 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="02"
            title="How Elva sounds on the phone"
            sub="Five preset voices. Tap any voice to hear a sample, then select the one that fits your practice."
            action={{ label: 'Browse voice library' }}
          />
          {RCPT_SETTINGS_VOICE_PRESETS.map((voice, i) => (
            <VoiceRow
              key={voice.id}
              voice={voice}
              selected={state.voice === voice.id}
              playing={playingVoice === voice.id}
              onSelect={(id) => upd('voice', id)}
              onPlay={handlePlay}
              isLast={i === RCPT_SETTINGS_VOICE_PRESETS.length - 1}
            />
          ))}
        </div>
      </section>

      <section className="set-fade-up set-d-3 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="03"
            title="How Elva carries herself"
            sub="Two sliders shape her default tone. Elva adapts mid-conversation — softens for anxious callers, tightens up for frustrated ones."
          />
          <SettingsFieldRow
            title="Warmth"
            description="Professional and crisp on one end, warm and conversational on the other."
            isDirty={dirty('warmth')}
          >
            <SettingsSlider
              value={state.warmth}
              onChange={(v) => upd('warmth', v)}
              leftLabel="Professional"
              rightLabel="Warm"
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Formality"
            description="Casual phrasing for friendly neighborhood practices; formal for clinical / specialty offices."
            isDirty={dirty('formality')}
            last
          >
            <SettingsSlider
              value={state.formality}
              onChange={(v) => upd('formality', v)}
              leftLabel="Casual"
              rightLabel="Formal"
            />
          </SettingsFieldRow>
        </div>
      </section>

      <section className="set-fade-up set-d-4 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="What Elva says when she answers"
            sub="The first thing every caller hears. Elva adapts the wording for SMS, email, and chat automatically."
          />
          <SettingsDirtyBlock
            isDirty={dirty('greetingBusiness')}
            className="border-b border-dashed border-[var(--cream-2)] px-6 py-[18px]"
          >
            <label className="mb-1 block text-[13.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
              Business hours greeting
            </label>
            <div className="mb-2.5 text-[12.5px] text-[var(--ink-2)]">
              Played during your open hours. Keep it short — patients want to talk, not listen.
            </div>
            <textarea
              className="set-textarea text-[14px] leading-[1.6]"
              value={state.greetingBusiness}
              onChange={(e) => upd('greetingBusiness', e.target.value)}
              rows={3}
            />
            <div className="mt-1.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">
              {state.greetingBusiness.length} / 200 characters
            </div>
          </SettingsDirtyBlock>
          <SettingsDirtyBlock isDirty={dirty('greetingAfterHours')} className="px-6 py-[18px]">
            <label className="mb-1 block text-[13.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
              After-hours greeting
            </label>
            <div className="mb-2.5 text-[12.5px] text-[var(--ink-2)]">
              Played outside open hours. Offers booking online, voicemail, or callback.
            </div>
            <textarea
              className="set-textarea text-[14px] leading-[1.6]"
              value={state.greetingAfterHours}
              onChange={(e) => upd('greetingAfterHours', e.target.value)}
              rows={3}
            />
            <div className="mt-1.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">
              {state.greetingAfterHours.length} / 250 characters
            </div>
          </SettingsDirtyBlock>
        </div>
      </section>

      <section className="set-fade-up set-d-5 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="05"
            title="Multilingual support"
            sub="Elva detects the patient's language automatically and switches mid-conversation."
          />
          <SettingsFieldRow
            title="Respond in the patient's language"
            description="When on, Elva mirrors whatever language the patient uses — switching to Spanish, Mandarin, Vietnamese, or any of 20+ supported languages mid-conversation."
            isDirty={dirty('multilingual')}
            last
          >
            <SettingsToggle value={state.multilingual} onChange={(v) => upd('multilingual', v)} />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
